import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Navigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load all non-homepage routes
const Services = lazy(() => import("./pages/Services"));
const Gallery = lazy(() => import("./pages/Gallery"));
const About = lazy(() => import("./pages/About"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const Contact = lazy(() => import("./pages/Contact"));
const Commercial = lazy(() => import("./pages/Commercial"));
// Service Pages
const HouseWashing = lazy(() => import("./pages/services/HouseWashing"));
const RoofWashing = lazy(() => import("./pages/services/RoofWashing"));
const GutterCleaning = lazy(() => import("./pages/services/GutterCleaning"));
const ConcreteWashing = lazy(() => import("./pages/services/ConcreteWashing"));
const WoodRestoration = lazy(() => import("./pages/services/WoodRestoration"));
const VinylFenceCleaning = lazy(() => import("./pages/services/VinylFenceCleaning"));
const OxidationRemoval = lazy(() => import("./pages/services/OxidationRemoval"));
const CommercialService = lazy(() => import("./pages/services/Commercial"));
const DumpsterPadCleaning = lazy(() => import("./pages/services/DumpsterPadCleaning"));
const FleetWashing = lazy(() => import("./pages/services/FleetWashing"));
const PressureWashing = lazy(() => import("./pages/services/PressureWashing"));
const DrivewayClean = lazy(() => import("./pages/services/DrivewayClean"));
const DeckCleaning = lazy(() => import("./pages/services/DeckCleaning"));
const PatioCleaning = lazy(() => import("./pages/services/PatioCleaning"));
const SidewalkCleaning = lazy(() => import("./pages/services/SidewalkCleaning"));
const BrickPaverCleaning = lazy(() => import("./pages/services/BrickPaverCleaning"));
const RustStainRemoval = lazy(() => import("./pages/services/RustStainRemoval"));
// Service Areas
const Inwood = lazy(() => import("./pages/service-areas/Inwood"));
const Martinsburg = lazy(() => import("./pages/service-areas/Martinsburg"));
const FallingWaters = lazy(() => import("./pages/service-areas/FallingWaters"));
const Hedgesville = lazy(() => import("./pages/service-areas/Hedgesville"));
const BerkeleySprings = lazy(() => import("./pages/service-areas/BerkeleySprings"));
const Charlestown = lazy(() => import("./pages/service-areas/Charlestown"));
const Ranson = lazy(() => import("./pages/service-areas/Ranson"));
const Kearneysville = lazy(() => import("./pages/service-areas/Kearneysville"));
const HarpersFerry = lazy(() => import("./pages/service-areas/HarpersFerry"));
const BunkerHill = lazy(() => import("./pages/service-areas/BunkerHill"));
const Shepherdstown = lazy(() => import("./pages/service-areas/Shepherdstown"));
const Winchester = lazy(() => import("./pages/service-areas/Winchester"));
const Berryville = lazy(() => import("./pages/service-areas/Berryville"));
const StephensCity = lazy(() => import("./pages/service-areas/StephensCity"));
// County Pages
const BerkeleyCounty = lazy(() => import("./pages/service-areas/BerkeleyCounty"));
const JeffersonCounty = lazy(() => import("./pages/service-areas/JeffersonCounty"));
const FrederickCounty = lazy(() => import("./pages/service-areas/FrederickCounty"));
const WashingtonCounty = lazy(() => import("./pages/service-areas/WashingtonCounty"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Review = lazy(() => import("./pages/Review"));
const MarketingForm = lazy(() => import("./pages/MarketingForm"));
const GetYourDiscount = lazy(() => import("./pages/GetYourDiscount"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LocationServiceRouter = lazy(() => import("./pages/LocationServiceRouter"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/pressure-washing" element={<PressureWashing />} />
            <Route path="/services/house-washing" element={<HouseWashing />} />
            <Route path="/services/roof-washing" element={<RoofWashing />} />
            <Route path="/services/gutter-cleaning" element={<GutterCleaning />} />
            <Route path="/services/concrete-cleaning" element={<ConcreteWashing />} />
            <Route path="/services/concrete-washing" element={<Navigate to="/services/concrete-cleaning" replace />} />
            <Route path="/services/driveway-cleaning" element={<DrivewayClean />} />
            <Route path="/services/deck-cleaning" element={<DeckCleaning />} />
            <Route path="/services/patio-cleaning" element={<PatioCleaning />} />
            <Route path="/services/sidewalk-cleaning" element={<SidewalkCleaning />} />
            <Route path="/services/brick-paver-cleaning" element={<BrickPaverCleaning />} />
            <Route path="/services/rust-stain-removal" element={<RustStainRemoval />} />
            <Route path="/services/wood-restoration" element={<WoodRestoration />} />
            <Route path="/services/vinyl-fence-cleaning" element={<VinylFenceCleaning />} />
            <Route path="/services/oxidation-removal" element={<OxidationRemoval />} />
            <Route path="/services/commercial" element={<CommercialService />} />
            <Route path="/services/dumpster-pad-cleaning" element={<DumpsterPadCleaning />} />
            <Route path="/services/fleet-washing" element={<FleetWashing />} />
            <Route path="/commercial" element={<Commercial />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about" element={<About />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            {/* Service Areas - West Virginia */}
            <Route path="/service-areas/inwood-wv" element={<Inwood />} />
            <Route path="/service-areas/martinsburg-wv" element={<Martinsburg />} />
            <Route path="/service-areas/falling-waters-wv" element={<FallingWaters />} />
            <Route path="/service-areas/hedgesville-wv" element={<Hedgesville />} />
            <Route path="/service-areas/berkeley-springs-wv" element={<BerkeleySprings />} />
            <Route path="/service-areas/charles-town-wv" element={<Charlestown />} />
            <Route path="/service-areas/ranson-wv" element={<Ranson />} />
            <Route path="/service-areas/kearneysville-wv" element={<Kearneysville />} />
            <Route path="/service-areas/harpers-ferry-wv" element={<HarpersFerry />} />
            <Route path="/service-areas/bunker-hill-wv" element={<BunkerHill />} />
            <Route path="/service-areas/shepherdstown-wv" element={<Shepherdstown />} />
            {/* Service Areas - Virginia */}
            <Route path="/service-areas/winchester-va" element={<Winchester />} />
            <Route path="/service-areas/berryville-va" element={<Berryville />} />
            <Route path="/service-areas/stephens-city-va" element={<StephensCity />} />
            {/* County Umbrella Pages */}
            <Route path="/service-areas/berkeley-county" element={<BerkeleyCounty />} />
            <Route path="/service-areas/jefferson-county" element={<JeffersonCounty />} />
            <Route path="/service-areas/frederick-county" element={<FrederickCounty />} />
            <Route path="/service-areas/washington-county" element={<WashingtonCounty />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/review" element={<Review />} />
            <Route path="/marketing-form" element={<MarketingForm />} />
            <Route path="/get-your-discount" element={<GetYourDiscount />} />
            {/* Soft 404 redirects */}
            <Route path="/blank-1" element={<Navigate to="/" replace />} />
            <Route path="/blank-3" element={<Navigate to="/" replace />} />
            <Route path="/general-clean" element={<Navigate to="/" replace />} />
            {/* Legacy slug redirects */}
            <Route path="/service-areas/inwood" element={<Navigate to="/service-areas/inwood-wv" replace />} />
            <Route path="/service-areas/martinsburg" element={<Navigate to="/service-areas/martinsburg-wv" replace />} />
            <Route path="/service-areas/falling-waters" element={<Navigate to="/service-areas/falling-waters-wv" replace />} />
            <Route path="/service-areas/hedgesville" element={<Navigate to="/service-areas/hedgesville-wv" replace />} />
            <Route path="/service-areas/berkeley-springs" element={<Navigate to="/service-areas/berkeley-springs-wv" replace />} />
            <Route path="/service-areas/charlestown" element={<Navigate to="/service-areas/charles-town-wv" replace />} />
            <Route path="/service-areas/ranson" element={<Navigate to="/service-areas/ranson-wv" replace />} />
            <Route path="/service-areas/kearneysville" element={<Navigate to="/service-areas/kearneysville-wv" replace />} />
            <Route path="/service-areas/harpers-ferry" element={<Navigate to="/service-areas/harpers-ferry-wv" replace />} />
            <Route path="/service-areas/bunker-hill" element={<Navigate to="/service-areas/bunker-hill-wv" replace />} />
            <Route path="/service-areas/shepherdstown" element={<Navigate to="/service-areas/shepherdstown-wv" replace />} />
            <Route path="/service-areas/winchester" element={<Navigate to="/service-areas/winchester-va" replace />} />
            <Route path="/service-areas/berryville" element={<Navigate to="/service-areas/berryville-va" replace />} />
            <Route path="/service-areas/stephens-city" element={<Navigate to="/service-areas/stephens-city-va" replace />} />
            {/* Location + Service SEO Pages (210 combos) */}
            <Route path="/service-areas/:locationSlug/:serviceSlug" element={<LocationServiceRouter />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
